import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { useState } from 'react';
import { CustomDefenseProvider, SimulationProvider, ThemeProvider, useTheme } from './context';
import { useAuth } from './hooks';
import HomePage from './pages/HomePage';
import SimulationPage from './pages/SimulationPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import AccountPage from './pages/AccountPage';
import AuthPage from './pages/AuthPage';
import TermsPage from './pages/TermsPage';
import PricingPage from './pages/PricingPage';
import DefenseBuilderPage from './pages/DefenseBuilderPage';
import './App.css';

// Theme toggle button component
function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  
  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <button 
      className="theme-toggle" 
      onClick={toggleTheme}
      aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
    >
      {resolvedTheme === 'dark' ? (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="5"/>
          <line x1="12" y1="1" x2="12" y2="3"/>
          <line x1="12" y1="21" x2="12" y2="23"/>
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
          <line x1="1" y1="12" x2="3" y2="12"/>
          <line x1="21" y1="12" x2="23" y2="12"/>
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
        </svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
        </svg>
      )}
    </button>
  );
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  return <>{children}</>;
}

function AppContent() {
  const { isAuthenticated, isLoading } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="App">
      <header className="app-header">
        <Link to="/" className="app-logo">PickSix Pro</Link>
        <button 
          className={`menu-toggle ${menuOpen ? 'open' : ''}`}
          type="button"
          onClick={() => setMenuOpen(!menuOpen)}
          // aria-expanded and aria-controls were both missing, so a screen reader user was told
          // nothing about whether the menu was open or what the button operated. The label states
          // the CURRENT state rather than the next action.
          aria-expanded={menuOpen}
          aria-controls="app-nav"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        >
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
        </button>
        <nav id="app-nav" className={`app-nav ${menuOpen ? 'open' : ''}`}>
          <Link to="/" className="nav-link" onClick={closeMenu}>Home</Link>
          <Link to="/simulator" className="nav-link" onClick={closeMenu}>Simulator</Link>
          <Link to="/about" className="nav-link" onClick={closeMenu}>About</Link>
          <Link to="/pricing" className="nav-link" onClick={closeMenu}>Pricing</Link>
          <Link to="/contact" className="nav-link" onClick={closeMenu}>Contact</Link>
          {!isLoading && (
            isAuthenticated ? (
              <Link to="/account" className="nav-link nav-link-account" onClick={closeMenu}>Account</Link>
            ) : (
              <>
                <ThemeToggle />
                <Link to="/auth" className="nav-link nav-link-signin" onClick={closeMenu}>Sign In</Link>
              </>
            )
          )}
          {!isLoading && isAuthenticated && <ThemeToggle />}
        </nav>
      </header>
      <main className="app-main">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/simulator" element={<SimulationPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route 
            path="/account" 
            element={
              <ProtectedRoute>
                <AccountPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/defense-builder" 
            element={
              <ProtectedRoute>
                <DefenseBuilderPage />
              </ProtectedRoute>
            } 
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <footer className="app-footer">
        <div className="footer-content">
          <span>Made by ProjectPS2 LLC</span>
          <Link to="/terms" className="footer-link">Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <CustomDefenseProvider>
        <SimulationProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </SimulationProvider>
      </CustomDefenseProvider>
    </ThemeProvider>
  );
}

export default App;
